import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  eachWeekOfInterval,
  format,
  isBefore,
  isSameDay,
} from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import Select from 'react-select';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { EventImpl } from '@fullcalendar/core/internal';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

interface PricingSummaryCalendarProps {
  tarifs: Array<{
    name?: string | null;
    periods: Array<{
      startDate: Date;
      endDate: Date;
      applicationDays: string[];
    }>;
  }>;
  categoryOptions?: Array<{
    value: { name: string; index: number };
    label: string;
  }>;
  currentCategoryIndex?: number;
  onCategoryChange?: (index: number) => void;
}

const PricingSummaryCalendar = ({
  tarifs,
  categoryOptions,
  currentCategoryIndex,
  onCategoryChange,
}: PricingSummaryCalendarProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const getTextColor = useCallback(
    (tarifIndex: number) =>
      tarifIndex % 4 === 0
        ? '#6941C6'
        : tarifIndex % 4 === 1
          ? '#15803D'
          : tarifIndex % 4 === 2
            ? '#1D4ED8'
            : '#C2410C',
    [],
  );

  const events = useMemo(() => {
    const eventList = tarifs.flatMap((tarif, tarifIndex) => {
      return tarif.periods.flatMap((period) => {
        const { startDate, endDate, applicationDays } = period;

        if (applicationDays.length === 7) {
          return [
            {
              title: tarif.name ?? '',
              start: startDate,
              end: addDays(endDate, 1),
              allDay: true,
              groupId: `${tarif.name ?? ''}-${tarifIndex}`,
              textColor: getTextColor(tarifIndex),
            },
          ];
        }

        const weeks = eachWeekOfInterval(
          {
            start: startDate,
            end: endDate,
          },
          { weekStartsOn: 1 },
        );

        const dayNameToIndex = {
          Lundi: 0,
          Mardi: 1,
          Mercredi: 2,
          Jeudi: 3,
          Vendredi: 4,
          Samedi: 5,
          Dimanche: 6,
        };

        const applicationDayIndexes = applicationDays.map(
          (day) => dayNameToIndex[day as keyof typeof dayNameToIndex],
        );

        const consecutivePeriods: number[][] = [];
        let currentPeriod = [];
        for (let i = 0; i < applicationDayIndexes.length; i++) {
          const currentDay = applicationDayIndexes[i];
          const nextDay = applicationDayIndexes[i + 1];

          currentPeriod.push(currentDay);

          if (nextDay !== currentDay + 1) {
            consecutivePeriods.push([...currentPeriod]);
            currentPeriod = [];
          }
        }
        if (currentPeriod.length > 0) {
          consecutivePeriods.push(currentPeriod);
        }

        const eventsForPeriod: {
          title: string;
          start: Date;
          end: Date;
          groupId: string;
          allDay?: boolean;
          textColor?: string;
        }[] = [];
        weeks.forEach((week) => {
          const startOfCurrentWeek = week;
          consecutivePeriods.forEach((consecutiveDaysPeriodIndexes) => {
            const periodStart = addDays(
              startOfCurrentWeek,
              consecutiveDaysPeriodIndexes[0],
            );
            const periodEnd = addDays(
              startOfCurrentWeek,
              consecutiveDaysPeriodIndexes[
                consecutiveDaysPeriodIndexes.length - 1
              ] + 1,
            );

            if (
              isBefore(startDate, periodStart) &&
              isBefore(periodStart, endDate)
            )
              eventsForPeriod.push({
                title: tarif.name ?? '',
                start: periodStart,
                end: isBefore(periodEnd, endDate)
                  ? periodEnd
                  : addDays(endDate, 1),
                allDay: true,
                groupId: `${tarif.name ?? ''}-${tarifIndex}`,
                textColor: getTextColor(tarifIndex),
              });
            else {
              const startOfPeriodIndex = differenceInCalendarDays(
                startDate,
                periodStart,
              );
              const firstDayInPeriodIndex = consecutiveDaysPeriodIndexes.find(
                (periodIndex) => periodIndex >= startOfPeriodIndex,
              );
              if (firstDayInPeriodIndex) {
                const firstDayInPeriod = addDays(
                  startOfCurrentWeek,
                  firstDayInPeriodIndex,
                );
                if (
                  isBefore(firstDayInPeriod, endDate) ||
                  isSameDay(firstDayInPeriod, endDate)
                )
                  eventsForPeriod.push({
                    title: tarif.name ?? '',
                    start: firstDayInPeriod,
                    end: isBefore(periodEnd, endDate)
                      ? periodEnd
                      : addDays(endDate, 1),
                    allDay: true,
                    groupId: `${tarif.name ?? ''}-${tarifIndex}`,
                    textColor: getTextColor(tarifIndex),
                  });
              }
            }
          });
        });

        return eventsForPeriod;
      });
    });

    const allDatesCovered = new Set<string>();

    eventList.forEach((event) => {
      if (event.start && event.end) {
        const current = new Date(event.start);
        while (current < event.end) {
          allDatesCovered.add(format(current, 'yyyy-MM-dd'));
          current.setDate(current.getDate() + 1);
        }
      }
    });

    if (eventList.length > 0) {
      const firstDate = new Date(
        Math.min(
          ...eventList
            .filter((event) => event.start && event.end)
            .map((event) => event.start.getTime()),
        ),
      );
      const lastDate = new Date(
        Math.max(
          ...eventList
            .filter((event) => event.start && event.end)
            .map((event) => event.end.getTime()),
        ),
      );

      const noEventDays = [];
      let consecutiveNoEventStart: Date | null = null;
      const currentDate = new Date(firstDate);
      while (currentDate < lastDate) {
        const isoDate = format(currentDate, 'yyyy-MM-dd');

        if (!allDatesCovered.has(isoDate)) {
          if (!consecutiveNoEventStart) {
            consecutiveNoEventStart = new Date(currentDate);
          }
        } else if (consecutiveNoEventStart) {
          noEventDays.push({
            title: '',
            start: new Date(consecutiveNoEventStart),
            end: new Date(currentDate),
            allDay: true,
            className:
              '!bg-[#C11515] !bg-warning-icon !bg-no-repeat !bg-center !bg-contain !border-[#A70A0A]',
            groupId: 'no-event',
          });
          consecutiveNoEventStart = null;
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (consecutiveNoEventStart) {
        noEventDays.push({
          title: '',
          start: new Date(consecutiveNoEventStart),
          end: addDays(new Date(currentDate), 1),
          allDay: true,
          className:
            '!bg-[#C11515] !bg-warning-icon !bg-no-repeat !bg-center !bg-contain !border-[#A70A0A]',
          groupId: 'no-event',
        });
      }

      eventList.push(...noEventDays);
    }

    return eventList;
  }, [tarifs, getTextColor]);

  const calendarRef = useRef<FullCalendar>(null);

  const [currentStartMonth, setCurrentStartMonth] = useState(new Date());

  const handleEventMouseEnter = useCallback(
    ({ event, el }: { event: EventImpl; el: HTMLElement }) => {
      tippy(el, {
        content:
          event.groupId === 'no-event'
            ? t('hotels.roomTarifs.noTarif')
            : event.title,
      });
    },
    [t],
  );

  const customMoreLinkText = useCallback(
    (number: number) => t('hotels.roomTarifs.moreTarifs', { number }),
    [t],
  );

  const [selectedEventGroupId, setSelectedEventGroupId] = useState<
    string | undefined
  >(undefined);
  const handleEventClick = useCallback(
    ({ event }: { event: EventImpl }) => {
      if (
        selectedEventGroupId !== event.groupId &&
        event.groupId !== 'no-event'
      )
        setSelectedEventGroupId(event.groupId);
      else setSelectedEventGroupId(undefined);
    },
    [selectedEventGroupId],
  );

  const customEventClassNames = useCallback(
    ({ event }: { event: EventImpl }) => {
      const eventName = event.title ?? '';
      const eventIndex = +event.groupId.slice(eventName.length + 1);
      return clsx('h-6 max-h-2 md:max-h-4 2xl:max-h-10', {
        '!bg-brand-50 !border-brand-500':
          event.groupId === selectedEventGroupId,
        '!bg-[#F9F5FF] !border-[#E9D7FE]':
          eventIndex % 4 === 0 && event.groupId !== selectedEventGroupId,
        '!bg-[#F0FDF4] !border-[#BBF7D0]':
          eventIndex % 4 === 1 && event.groupId !== selectedEventGroupId,
        '!bg-[#EFF6FF] !border-[#BFDBFE]':
          eventIndex % 4 === 2 && event.groupId !== selectedEventGroupId,
        '!bg-[#FFF7ED] !border-[#FED7AA]':
          eventIndex % 4 === 3 && event.groupId !== selectedEventGroupId,
      });
    },
    [selectedEventGroupId],
  );

  return (
    <div className="flex flex-col gap-4 bg-white m-1 md:m-8 border rounded-lg border-gray-300">
      <div className="flex flex-col md:flex-row justify-between gap-4 px-6 py-5">
        <div className="min-w-52">
          {categoryOptions &&
            currentCategoryIndex !== undefined &&
            onCategoryChange && (
              <Select
                noOptionsMessage={() => t('hotels.roomTarifs.noOptionsFound')}
                options={categoryOptions}
                onChange={(newValue) => {
                  if (newValue?.value) {
                    onCategoryChange(newValue.value.index);
                  }
                }}
                value={categoryOptions[currentCategoryIndex]}
                className="w-full z-10 rounded-lg border-gray-300 gap-2 text-lg leading-6 text-gray-900 placeholder-gray-500 focus-visible:outline-hidden focus-visible:shadow-active focus-visible:ring-offset-2 focus-visible:border-brand-300 disabled:bg-gray-100"
                styles={{
                  indicatorSeparator: (base) => ({
                    ...base,
                    display: 'none',
                  }),
                  control: (base, { isDisabled }) => ({
                    ...base,
                    backgroundColor: isDisabled
                      ? 'var(--gray-100)'
                      : base.backgroundColor,
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: 'var(--gray-900)',
                  }),
                }}
              />
            )}
        </div>
        <div className="flex gap-6 justify-center items-center">
          <StyledTooltipContainer
            tooltipMessage={t('hotels.roomTarifs.previousYear')}
          >
            <ArrowLeftIcon
              stroke="black"
              className="size-6 hover:cursor-pointer"
              onClick={() => {
                calendarRef.current?.getApi().prev();
                setCurrentStartMonth(addYears(currentStartMonth, -1));
              }}
            />
          </StyledTooltipContainer>
          <span className="text-xl font-semibold leading-6">
            {format(currentStartMonth, 'LLLL yyy', {
              locale: LOCALES_MAP[currentLanguage],
            })}{' '}
            -{' '}
            {format(addMonths(currentStartMonth, 11), 'LLLL yyy', {
              locale: LOCALES_MAP[currentLanguage],
            })}
          </span>
          <StyledTooltipContainer
            tooltipMessage={t('hotels.roomTarifs.nextYear')}
          >
            <ArrowRightIcon
              stroke="black"
              className="size-6 hover:cursor-pointer"
              onClick={() => {
                calendarRef.current?.getApi().next();
                setCurrentStartMonth(addYears(currentStartMonth, 1));
              }}
            />
          </StyledTooltipContainer>
        </div>
      </div>
      <FullCalendar
        plugins={[multiMonthPlugin, interactionPlugin]}
        initialView="multiMonthTwelveMonths"
        views={{
          multiMonthTwelveMonths: {
            type: 'multiMonth',
            duration: { months: 12 },
          },
        }}
        events={events}
        locale={currentLanguage}
        firstDay={1}
        headerToolbar={false}
        ref={calendarRef}
        displayEventTime={false}
        moreLinkText={customMoreLinkText}
        moreLinkClassNames="h-6 px-2 py-1"
        eventMouseEnter={handleEventMouseEnter}
        eventClick={handleEventClick}
        eventClassNames={customEventClassNames}
      />
    </div>
  );
};

export default PricingSummaryCalendar;
